import angular from 'angular';

import { MERCHANT_PORTAL_API_BASE_URL } from "../../core/main/controller/environment";
import Parse from '../../core/main/factory/parse';
import Storage from '../../core/main/factory/storage';

import '../controller/controller';
import '../../core/main/factory/country';
import '../../core/main/factory/feature';
import '../../core/main/factory/analytics';
import './device';

const nilOperation = () => {/* used for the callback if none is given */
}
const getUser = () => Storage.getLocalStorageItem("merchantUser") || {}
const setUser = (merchantUser) => Storage.setLocalStorageItem("merchantUser", merchantUser)
const removeUser = () => Storage.removeLocalStorageItem("merchantUser")
const getUserEmail = () => Storage.getLocalStorageItem("merchantUserEmail")
const setUserEmail = (merchantUserEmail) => Storage.setLocalStorageItem("merchantUserEmail", merchantUserEmail)
const removeUserEmail = () => Storage.removeLocalStorageItem("merchantUserEmail")
const getMerchant = () => Storage.getLocalStorageItem("merchantDetails") || {}
const setMerchant = (merchantDetails) => Storage.setLocalStorageItem("merchantDetails", merchantDetails)
const removeMerchant = () => Storage.removeLocalStorageItem("merchantDetails")
const getAvailableMerchants = () => Storage.getLocalStorageItem("availableMerchants") || []
const setAvailableMerchants = (merchants) => Storage.setLocalStorageItem("availableMerchants", merchants)
const removeAvailableMerchants = () => Storage.removeLocalStorageItem("availableMerchants")
const setIsSsoUser = (authType) => Storage.setLocalStorageItem("ssoUser", authType)
const getIsSsoUser = () => Storage.getLocalStorageItem("ssoUser") || false
const removeIsSsoUser = () => Storage.removeLocalStorageItem("ssoUser")
const setDefaultMerchantId = (defaultMerchantId) => Storage.setLocalStorageItem("defaultMerchantId", defaultMerchantId)
const getPermissions = () => getMerchant().permissions || [];
const getChannels = () => getMerchant().enabledChannels?.map(({channelType}) => channelType) || [];
const setHubPermissions = (hubPermissions) => Storage.setLocalStorageItem("hubPermissions", hubPermissions)
const getHubPermissions = () => Storage.getLocalStorageItem("hubPermissions") || {}

const getDefaultMerchantId = () => {
    const defaultMerchantId = Storage.getLocalStorageItem("defaultMerchantId");
    if (defaultMerchantId) {
        return parseInt(defaultMerchantId);
    }
    return null;
}

const setDefaultMerchant = () => {
    const availableMerchants = getAvailableMerchants();
    const existingDefaultMerchantId = getDefaultMerchantId();
    const defaultMerchant = availableMerchants.find((merchant) => merchant.id === existingDefaultMerchantId)
        || availableMerchants[0]

    setMerchant(defaultMerchant);
    setDefaultMerchantId(defaultMerchant.id);
};

const setMerchantData = function (merchants) {
    if (merchants && merchants.length === 0) {
        return;
    }
    setAvailableMerchants(merchants);
    setDefaultMerchant();
};

const setData = (response) => {
    setUser(response.data.user || {})
    setMerchantData(response.data.merchants)
    setHubPermissions(response.data.hubUserPermissions)
}

const Authentication = {
    maximumLoginTime: 3600000, // 1 hour in milliseconds.
    getDefaultMerchantId,
    loggedIn: () => Object.keys(getUser()).length > 0,
    displayName: () => getUser().name || "",
    userId: () => getUser().id || "",
    availableMerchants: () => getAvailableMerchants(),
    currentMerchant: () => getMerchant(),
    hasManyMerchants: () => getAvailableMerchants().length > 1,
    tradingName: () => getMerchant().tradingName || "",
    countryCode: () => getMerchant().countryCode || "",
    currencyCode: () => getMerchant().currencyUnit || "",
    merchantId: () => getMerchant().id || "",
    standalonePosDevice: () => getMerchant().standalonePosDevice,
    ssoUser: () => getIsSsoUser(),
    userEmail: () => getUserEmail(),
    loginHasExpired: () => Date.now() >= getUser().expiry,
    hasPermission: (permission) => getPermissions().includes(permission),
    hasChannel: (channelName) => getChannels().includes(channelName),

    switchMerchant: (merchant) => {
        setMerchant(merchant);
        setDefaultMerchantId(merchant.id);
    },

    extendLoginExpiry: () => {
        const user = getUser();
        user.expiry = Date.now() + this.maximumLoginTime;
        setUser(user);
        return user.expiry;
    },

    hasAnyPermissions(...permissions) {
        return permissions.flat().some(this.hasPermission)
    },

    hasAnyChannel(...channelNames) {
        return channelNames.flat().some(this.hasChannel)
    },
}

export default Authentication

angular.module("merchant.application.main").factory("merchant.application.main.factory.authentication", [
    "$rootScope", "$http", "$resource", "$window", "$q",
    "core.application.main.factory.country",
    "core.application.main.factory.analytics",
    "core.application.main.factory.feature",
    "merchant.application.main.factory.device",
    "merchant.application.main.factory.okta",
    function ($rootScope, $http, $resource, $window, $q, Country, Analytics, Feature, Device, Okta) {

        var expose = {};
        const apiEndpoint = MERCHANT_PORTAL_API_BASE_URL;
        const loginEndpoint = apiEndpoint + "/portal/merchants/auth/login";
        const logoutEndpoint = apiEndpoint + "/portal/merchants/auth/logout";
        const verifyEndpoint = apiEndpoint + "/portal/merchants/auth/verify";
        const resetAskResource = $resource(apiEndpoint + "/portal/merchants/auth/reset/send-email");
        const resetValidateTokenResource = $resource(apiEndpoint + "/portal/merchants/auth/reset/validate-token");
        const resetResource = $resource(apiEndpoint + "/portal/merchants/auth/reset");

        expose.maximumLoginTime = Authentication.maximumLoginTime;
        expose.getDefaultMerchantId = Authentication.getDefaultMerchantId;
        expose.loggedIn = Authentication.loggedIn;
        expose.loginHasExpired = Authentication.loginHasExpired;
        expose.hasPermission = Authentication.hasPermission;
        expose.hasAnyPermissions = Authentication.hasAnyPermissions;
        expose.hasChannel = Authentication.hasChannel;
        expose.hasAnyChannel = Authentication.hasAnyChannel;
        expose.displayName = Authentication.displayName;
        expose.userId = Authentication.userId;
        expose.availableMerchants = Authentication.availableMerchants;
        expose.currentMerchant = Authentication.currentMerchant;
        expose.hasManyMerchants = Authentication.hasManyMerchants;
        expose.tradingName = Authentication.tradingName;
        expose.countryCode = Authentication.countryCode;
        expose.currencyCode = Authentication.currencyCode;
        expose.merchantId = Authentication.merchantId;
        expose.standalonePosDevice = Authentication.standalonePosDevice;
        expose.ssoUser = Authentication.ssoUser;
        expose.userEmail = Authentication.userEmail;

        expose.switchMerchant = (merchant) => {
            Analytics.sendAmplitudeEvent(`Switched merchant`, merchant)
            Authentication.switchMerchant(merchant);
        }

        expose.extendLoginExpiry = () => {
            const user = getUser();
            user.expiry = Date.now() + expose.maximumLoginTime;
            setUser(user);
            expose.identify();
            return user.expiry;
        };

        expose.identify = () => {
            const userProperties = {
                email: getUserEmail(),
                currentMerchant: getDefaultMerchantId(),
                roles: getMerchant().roles,
                permissions: getMerchant().permissions,
                hubPermissions: getHubPermissions()[getDefaultMerchantId()]?.permissions || [],
                canAccessHub: !!getHubPermissions()[getDefaultMerchantId()]?.permissions.length
            }
            Analytics.identifyAmplitude(getUser().id, userProperties)
            var featureFlagPromise = Feature.retrieveFeatureFlagPromise("chameleon-enabled", false)
            $q.when(featureFlagPromise).then(function (flag) {
                if (flag) {
                    Analytics.identifyChameleonUser(getUser().id, userProperties)
                }
            })
        }

        expose.verify = (callback) => {
            callback = callback || nilOperation;
            $http({
                method: "POST",
                url: verifyEndpoint,
                headers: {"Content-Type": "application/x-www-form-urlencoded"},
            }).then(function (response) {
                setData(response);
                setIsSsoUser(response.data.authType == "BEARER")

                expose.extendLoginExpiry();
                broadcastEvents();
                callback(true);
            }).catch(function (error) {
                callback(false, error);
            });
        }

        expose.login = function (credentials, callback) {
            const userEmail = credentials && credentials.username;
            if (credentials) {
                callback = callback || nilOperation;
                credentials = Parse.encode(credentials);
                $http({
                    method: "POST",
                    url: loginEndpoint,
                    data: credentials,
                    headers: {"Content-Type": "application/x-www-form-urlencoded"},
                }).then(function (response) {
                    setUserEmail(userEmail);
                    setData(response);
                    setIsSsoUser(false);

                    expose.extendLoginExpiry();
                    broadcastEvents();

                    const merchantUserId = response.data.user.id;
                    const merchants = response.data.merchants.map(m => ({
                            merchantId: m.id,
                            countryCode: m.countryCode,
                            currencyUnit: m.currencyUnit,
                            enabledChannels: m.enabledChannels,
                            agencyRef: m.agencyRef || ""
                    }));
                    const eventProperties = {
                        merchantUserId,
                        merchants
                    }
                    Analytics.sendAmplitudeEvent("Merchant User Login Successful", eventProperties)
                    callback(true, response.data);
                }).catch(function (error) {
                    callback(false, error && error.data);
                });
            }
        };

        expose.oktaLogin = function (userEmail, accessToken, callback) {
            callback = callback || nilOperation;
            $http({
                method: "POST",
                url: loginEndpoint,
                headers: {
                    Authorization: "Bearer " + accessToken.value
                },
            }).then(function (response) {
                setUserEmail(userEmail);
                setData(response);
                setIsSsoUser(true);

                expose.extendLoginExpiry();
                broadcastEvents();

                callback(true, response.data);
            }).catch(function () {
                callback(false);
            });
        };

        const broadcastEvents = function () {
            $rootScope.$broadcast('merchantChanged');
            $rootScope.$broadcast('availableMerchantsChanged');
        };

        expose.logout = function (callback) {
            removeUser();
            removeUserEmail();
            removeAvailableMerchants();
            removeMerchant();

            Analytics.sendAmplitudeEvent("Merchant User Logout Successful")
            Analytics.deIdentifyAmplitude()
            var featureFlagPromise = Feature.retrieveFeatureFlagPromise("chameleon-enabled", false)
            $q.when(featureFlagPromise).then(function (flag) {
                if (flag) {
                    Analytics.deIdentifyChameleonUser()
                }
            });

            Device.deviceToken(null);
            callback = callback || nilOperation;
            $http({
                method: "POST",
                url: logoutEndpoint
            }).then(function () {
                callback();
            }).catch(function () {
                callback();
            });
        };

        expose.oktaLogout = function (redirectUri) {
            return Promise.resolve(removeIsSsoUser())
                .then(() => {
                    Analytics.deIdentifyAmplitude()
                    var featureFlagPromise = Feature.retrieveFeatureFlagPromise("chameleon-enabled", false);
                    $q.when(featureFlagPromise).then(function (flag) {
                        if (flag) {
                            Analytics.deIdentifyChameleonUser();
                        }
                    });
                    return Okta.logout()
                })
                .then((idToken) => {
                    $window.location.href = Okta.logoutRedirectUrl(idToken, redirectUri);
                });
        };

        expose.saveReset = function (parameters, success, error) {
            // expected parameters
            // {
            //     token: "xxxx",
            //     password: "xxxxxx",
            //     confirmPassword": "xxxxxx"
            // }
            // PAYL-10261: passing countryCode so that we don't depend on merchant
            parameters.countryCode = Country.getCountryCodeFromUrl();
            new resetResource(parameters).$save(success, error);
        };

        expose.saveResetAsk = function (email, success, error) {
            const countryCode = Country.getCountryCodeFromUrl();
            // PAYL-10261: passing countryCode so that we don't depend on merchant
            new resetAskResource({email, countryCode}).$save(success, error);
        };

        expose.saveResetValidateToken = function (token, success, error) {
            new resetValidateTokenResource({token: token}).$save(success, error);
        };

        return expose;
    }
]);
